import type { PatientContractId } from "../../../shared/schema/schema";
import { type AnswerWrite } from "../question-types/answerWrites";
import { type PatientContractDiagnosisCode, sortCodesLikePdf } from "./contractDiagnosisCodeForm";

// What an icdCodes / finalIcdCodes question prints, and the answer snapshot the review writes for
// it. The server's PDF (getCompleteDocumentWithAnswersByScheduledDocId) prints the document's
// stored icdCodes answer when there is one (`null` -> no codes), else the live codes of the task's
// contract. Pure (no React / app runtime) so it is node-tested.

/** A stored answer row of the question: its `answer`. `null` when the question has no row. */
export type IcdSnapshot = { answer: unknown } | null;

/** The codes of the task's contract, in the PDF's order. */
export function contractIcdCodes<T extends PatientContractDiagnosisCode>(
  codes: ReadonlyArray<T>,
  patientContractId: PatientContractId
): T[] {
  return sortCodesLikePdf(codes.filter((code) => code.patientContractId === patientContractId));
}

/**
 * The codes the PDF prints: the stored snapshot as is (a non-array, e.g. `null`, prints none), or,
 * without a snapshot, the live codes of the task's contract.
 */
export function icdCodesToPrint(
  snapshot: IcdSnapshot,
  liveCodes: ReadonlyArray<PatientContractDiagnosisCode>,
  patientContractId: PatientContractId
): unknown[] {
  if (snapshot !== null) {
    return Array.isArray(snapshot.answer) ? (snapshot.answer as unknown[]) : [];
  }
  return contractIcdCodes(liveCodes, patientContractId);
}

/**
 * The answer write that refreshes the question's snapshot after a code changed: the current codes
 * of the task's contract (the `PatientContractDiagnosisCode[]` shape the classic modal saves), at
 * the row its answer is stored at (`chartRow`, -1 when it has none yet).
 */
export function icdSnapshotWrite(
  question: { chartRow: number; id: number },
  codes: ReadonlyArray<PatientContractDiagnosisCode>,
  patientContractId: PatientContractId
): AnswerWrite {
  return {
    answer: contractIcdCodes(codes, patientContractId),
    chartRow: question.chartRow,
    questionId: question.id,
  };
}
